import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  apiChangePassword,
  apiGetWorkspaceSettings,
  apiPatchWorkspaceSettings,
  apiUploadAvatar,
  mapWorkspaceSettingsResponse,
} from '../api/client';
import { sampleProfileFields, sampleWorkspaceFields } from '../lib/sampleData';
import UserAvatar from '../components/UserAvatar';

const NOTIFICATION_ROWS = [
  { key: 'taskAssigned', labelKey: 'notificationTaskAssigned' },
  { key: 'commentMentioned', labelKey: 'notificationCommentMentioned' },
  { key: 'dueDateApproaching', labelKey: 'notificationDueDateApproaching' },
  { key: 'meetingLinkShared', labelKey: 'notificationMeetingLinkShared' },
];

const appearanceModeKeys = {
  Light: 'modeLight',
  Dark: 'modeDark',
  System: 'modeSystem',
};

const fontSizeKeys = {
  Small: 'fontSmall',
  Medium: 'fontMedium',
  Large: 'fontLarge',
};

function SettingsPage({ t, onToast, onWorkspaceAppearance }) {
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [savingTab, setSavingTab] = useState(null);
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    bio: '',
    timezone: 'UTC',
  });
  const [security, setSecurity] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    twoFactor: false,
  });
  const [notificationPrefs, setNotificationPrefs] = useState({
    taskAssigned: { email: true, inApp: true, push: true },
    commentMentioned: { email: true, inApp: true, push: false },
    dueDateApproaching: { email: true, inApp: true, push: true },
    meetingLinkShared: { email: false, inApp: true, push: false },
  });
  const [appearance, setAppearance] = useState({
    mode: 'System',
    accent: '#1A5FB4',
    fontSize: 'Medium',
    compactSidebar: false,
  });
  const [workspace, setWorkspace] = useState({
    name: 'CoreTask Workspace',
    invitation: 'Admins and Managers',
    defaultProjectPermission: 'Member',
  });
  const [integrations, setIntegrations] = useState([]);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const raw = await apiGetWorkspaceSettings();
      const mapped = mapWorkspaceSettingsResponse(raw);
      setProfile(mapped.profile);
      setNotificationPrefs(mapped.notificationPrefs);
      setAppearance(mapped.appearance);
      setWorkspace(mapped.workspace);
      setIntegrations(mapped.integrations);
      setSecurity((prev) => ({ ...prev, twoFactor: mapped.security.twoFactor }));
      onWorkspaceAppearance?.(mapped.appearance);
    } catch (err) {
      onToast?.(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [onToast, onWorkspaceAppearance]);

  useEffect(() => {
    void load();
  }, [load]);

  const runSave = async (tabKey, fn) => {
    setSavingTab(tabKey);
    try {
      await fn();
      await load();
    } catch (err) {
      onToast?.(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingTab(null);
    }
  };

  const handleAvatarFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';
    if (!file.type.startsWith('image/')) {
      onToast?.('Please select an image file (JPEG, PNG, GIF, or WebP).');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      onToast?.('Image must be 5 MB or smaller.');
      return;
    }
    setUploadingAvatar(true);
    try {
      const result = await apiUploadAvatar(file);
      const newUrl = result?.avatarUrl ?? result?.AvatarUrl ?? null;
      if (newUrl) {
        setProfile((prev) => ({ ...prev, avatarUrl: newUrl }));
        window.dispatchEvent(new CustomEvent('coretask:avatar-updated', { detail: { avatarUrl: newUrl } }));
      }
    } catch (err) {
      onToast?.(err instanceof Error ? err.message : String(err));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const profilePatch = useMemo(
    () => ({
      displayName: profile.name.trim(),
      bio: profile.bio.trim(),
      timezone: profile.timezone.trim() || 'UTC',
    }),
    [profile.name, profile.bio, profile.timezone],
  );

  const settingTabs = [
    { id: 'profile', label: t('settingsProfile') },
    { id: 'security', label: t('settingsSecurity') },
    { id: 'notifications', label: t('settingsNotifications') },
    { id: 'appearance', label: t('settingsAppearance') },
    { id: 'workspace', label: t('settingsWorkspace') },
    { id: 'integrations', label: t('settingsIntegrations') },
  ];

  return (
    <section className="grid gap-4 lg:grid-cols-12">
      <aside className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900 lg:col-span-3">
        <h2 className="mb-3 text-lg font-semibold text-slate-900 dark:text-white">{t('settingsTitle')}</h2>
        <div className="space-y-1.5">
          {settingTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`w-full rounded-lg px-2.5 py-2 text-left text-xs font-medium ${
                activeTab === tab.id
                  ? 'bg-brand-500 text-white'
                  : 'text-slate-600 hover:bg-brand-50 dark:text-slate-300 dark:hover:bg-brand-900/20'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </aside>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900 lg:col-span-9">
        {loading ? (
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('loadingSettings')}</p>
        ) : (
          <>
            {activeTab === 'profile' && (
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-slate-900 dark:text-white">{t('profileSettingsTitle')}</h3>
                <div className="flex items-center gap-3">
                  <UserAvatar
                    name={profile.name}
                    email={profile.email}
                    imageUrl={profile.avatarUrl ?? profile.avatar ?? null}
                    sizeClass="h-12 w-12"
                  />
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    onChange={handleAvatarFileChange}
                  />
                  <button
                    type="button"
                    disabled={uploadingAvatar}
                    onClick={() => avatarInputRef.current?.click()}
                    className="rounded-lg border border-brand-200 px-2.5 py-1 text-xs hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-brand-900 dark:hover:bg-brand-900/20"
                  >
                    {uploadingAvatar ? t('uploading') : t('uploadAvatar')}
                  </button>
                </div>
                <div className="grid gap-2 md:grid-cols-2 text-xs">
                  <input
                    value={profile.name}
                    onChange={(event) => setProfile((prev) => ({ ...prev, name: event.target.value }))}
                    className="rounded-lg border border-brand-200 bg-white px-3 py-2 dark:border-brand-900 dark:bg-slate-950"
                    placeholder={t('displayName')}
                  />
                  <input
                    value={profile.email}
                    readOnly
                    className="cursor-not-allowed rounded-lg border border-brand-200 bg-slate-50 px-3 py-2 dark:border-brand-900 dark:bg-slate-900"
                    placeholder={t('email')}
                    title={t('signInEmailTitle')}
                  />
                  <input
                    value={profile.timezone}
                    onChange={(event) => setProfile((prev) => ({ ...prev, timezone: event.target.value }))}
                    className="rounded-lg border border-brand-200 bg-white px-3 py-2 dark:border-brand-900 dark:bg-slate-950 md:col-span-2"
                    placeholder={t('timezonePlaceholder')}
                  />
                  <textarea
                    rows={3}
                    value={profile.bio}
                    onChange={(event) => setProfile((prev) => ({ ...prev, bio: event.target.value }))}
                    className="rounded-lg border border-brand-200 bg-white px-3 py-2 dark:border-brand-900 dark:bg-slate-950 md:col-span-2"
                    placeholder={t('bio')}
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setProfile((prev) => ({
                        ...prev,
                        ...sampleProfileFields(),
                      }))
                    }
                    className="rounded-lg border border-dashed border-brand-300 px-3 py-1.5 text-xs font-medium text-brand-800 hover:bg-brand-50 dark:border-brand-700 dark:text-brand-200 dark:hover:bg-brand-950/50"
                  >
                    {t('fillSampleData')}
                  </button>
                  <button
                    type="button"
                    disabled={savingTab === 'profile'}
                    onClick={() =>
                      runSave('profile', () => apiPatchWorkspaceSettings({ profile: profilePatch }))
                    }
                    className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                  >
                    {savingTab === 'profile' ? '…' : t('saveProfile')}
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-slate-900 dark:text-white">{t('settingsSecurity')}</h3>
                <div className="grid gap-2 md:grid-cols-2 text-xs">
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={security.currentPassword}
                    onChange={(event) =>
                      setSecurity((prev) => ({ ...prev, currentPassword: event.target.value }))
                    }
                    className="rounded-lg border border-brand-200 bg-white px-3 py-2 dark:border-brand-900 dark:bg-slate-950"
                    placeholder={t('currentPassword')}
                  />
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={security.newPassword}
                    onChange={(event) => setSecurity((prev) => ({ ...prev, newPassword: event.target.value }))}
                    className="rounded-lg border border-brand-200 bg-white px-3 py-2 dark:border-brand-900 dark:bg-slate-950"
                    placeholder={t('newPassword')}
                  />
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={security.confirmPassword}
                    onChange={(event) =>
                      setSecurity((prev) => ({ ...prev, confirmPassword: event.target.value }))
                    }
                    className="rounded-lg border border-brand-200 bg-white px-3 py-2 dark:border-brand-900 dark:bg-slate-950 md:col-span-2"
                    placeholder={t('confirmNewPassword')}
                  />
                </div>
                <button
                  type="button"
                  disabled={savingTab === 'password'}
                  onClick={() =>
                    runSave('password', async () => {
                      if (security.newPassword !== security.confirmPassword) {
                        throw new Error(t('passwordMismatch'));
                      }
                      if (security.newPassword.length < 8) {
                        throw new Error(t('passwordMinLength'));
                      }
                      await apiChangePassword({
                        currentPassword: security.currentPassword,
                        newPassword: security.newPassword,
                      });
                      setSecurity((prev) => ({
                        ...prev,
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: '',
                      }));
                      onToast?.(t('passwordUpdated'), 'success');
                    })
                  }
                  className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                >
                  {savingTab === 'password' ? '…' : t('updatePassword')}
                </button>
                <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={security.twoFactor}
                    onChange={(event) =>
                      setSecurity((prev) => ({ ...prev, twoFactor: event.target.checked }))
                    }
                  />
                  {t('twoFactorPreference')}
                </label>
                <button
                  type="button"
                  disabled={savingTab === 'securityPrefs'}
                  onClick={() =>
                    runSave('securityPrefs', () =>
                      apiPatchWorkspaceSettings({
                        security: { twoFactorEnabled: security.twoFactor },
                      }),
                    )
                  }
                  className="rounded-lg border border-brand-200 px-3 py-1.5 text-xs dark:border-brand-900 disabled:opacity-60"
                >
                  {savingTab === 'securityPrefs' ? '…' : t('saveChanges')}
                </button>
                <div className="rounded-lg border border-brand-200 p-3 text-xs dark:border-brand-900">
                  <p className="font-medium text-slate-700 dark:text-slate-200">{t('activeSessions')}</p>
                  <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                    {t('sessionsComingSoon')}
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-slate-900 dark:text-white">{t('settingsNotificationsTitle')}</h3>
                <div className="overflow-x-auto rounded-xl border border-brand-200 dark:border-brand-900">
                  <table className="min-w-full text-xs">
                    <thead className="bg-brand-50 dark:bg-brand-900/20">
                      <tr className="text-left text-slate-500 dark:text-slate-300">
                        <th className="px-3 py-2">{t('type')}</th>
                        <th className="px-3 py-2">{t('email')}</th>
                        <th className="px-3 py-2">{t('inApp')}</th>
                        <th className="px-3 py-2">{t('push')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {NOTIFICATION_ROWS.map(({ key, labelKey }) => {
                        const pref = notificationPrefs[key];
                        return (
                          <tr key={key} className="border-t border-brand-100 dark:border-brand-900/30">
                            <td className="px-3 py-2 text-slate-700 dark:text-slate-200">{t(labelKey)}</td>
                            {['email', 'inApp', 'push'].map((channel) => (
                              <td key={channel} className="px-3 py-2">
                                <input
                                  type="checkbox"
                                  checked={pref[channel]}
                                  onChange={(event) =>
                                    setNotificationPrefs((prev) => ({
                                      ...prev,
                                      [key]: { ...prev[key], [channel]: event.target.checked },
                                    }))
                                  }
                                />
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <button
                  type="button"
                  disabled={savingTab === 'notifications'}
                  onClick={() =>
                    runSave('notifications', () =>
                      apiPatchWorkspaceSettings({
                        notificationPrefs: {
                          taskAssigned: {
                            email: notificationPrefs.taskAssigned.email,
                            inApp: notificationPrefs.taskAssigned.inApp,
                            push: notificationPrefs.taskAssigned.push,
                          },
                          commentMentioned: {
                            email: notificationPrefs.commentMentioned.email,
                            inApp: notificationPrefs.commentMentioned.inApp,
                            push: notificationPrefs.commentMentioned.push,
                          },
                          dueDateApproaching: {
                            email: notificationPrefs.dueDateApproaching.email,
                            inApp: notificationPrefs.dueDateApproaching.inApp,
                            push: notificationPrefs.dueDateApproaching.push,
                          },
                          meetingLinkShared: {
                            email: notificationPrefs.meetingLinkShared.email,
                            inApp: notificationPrefs.meetingLinkShared.inApp,
                            push: notificationPrefs.meetingLinkShared.push,
                          },
                        },
                      }),
                    )
                  }
                  className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                >
                  {savingTab === 'notifications' ? '…' : t('saveChanges')}
                </button>
              </div>
            )}

            {activeTab === 'appearance' && (
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-slate-900 dark:text-white">{t('settingsAppearance')}</h3>
                <div className="grid gap-2 md:grid-cols-3">
                  {['Light', 'Dark', 'System'].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setAppearance((prev) => ({ ...prev, mode }))}
                      className={`rounded-xl border p-3 text-xs ${
                        appearance.mode === mode
                          ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                          : 'border-brand-200 dark:border-brand-900'
                      }`}
                    >
                      {t(appearanceModeKeys[mode])}
                    </button>
                  ))}
                </div>
                <label className="block text-xs">
                  <span className="mb-1 block text-slate-500 dark:text-slate-300">{t('accentColor')}</span>
                  <input
                    type="color"
                    value={appearance.accent}
                    onChange={(event) => setAppearance((prev) => ({ ...prev, accent: event.target.value }))}
                    className="h-9 w-full max-w-[200px] rounded-lg border border-brand-200 p-1 dark:border-brand-900 dark:bg-slate-950"
                  />
                </label>
                <label className="block text-xs">
                  <span className="mb-1 block text-slate-500 dark:text-slate-300">{t('fontSize')}</span>
                  <select
                    value={appearance.fontSize}
                    onChange={(event) => setAppearance((prev) => ({ ...prev, fontSize: event.target.value }))}
                    className="w-full max-w-xs rounded-lg border border-brand-200 bg-white px-3 py-2 dark:border-brand-900 dark:bg-slate-950"
                  >
                    {['Small', 'Medium', 'Large'].map((size) => (
                      <option key={size} value={size}>
                        {t(fontSizeKeys[size])}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={appearance.compactSidebar}
                    onChange={(event) =>
                      setAppearance((prev) => ({ ...prev, compactSidebar: event.target.checked }))
                    }
                  />
                  {t('sidebarCompactMode')}
                </label>
                <button
                  type="button"
                  disabled={savingTab === 'appearance'}
                  onClick={() =>
                    runSave('appearance', async () => {
                      await apiPatchWorkspaceSettings({
                        appearance: {
                          mode: appearance.mode,
                          accent: appearance.accent,
                          fontSize: appearance.fontSize,
                          compactSidebar: appearance.compactSidebar,
                        },
                      });
                      onWorkspaceAppearance?.(appearance);
                    })
                  }
                  className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                >
                  {savingTab === 'appearance' ? '…' : t('saveChanges')}
                </button>
              </div>
            )}

            {activeTab === 'workspace' && (
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-slate-900 dark:text-white">{t('workspaceSettingsAdmin')}</h3>
                <div className="grid gap-2 text-xs md:grid-cols-2">
                  <input
                    value={workspace.name}
                    onChange={(event) => setWorkspace((prev) => ({ ...prev, name: event.target.value }))}
                    className="rounded-lg border border-brand-200 bg-white px-3 py-2 dark:border-brand-900 dark:bg-slate-950 md:col-span-2"
                    placeholder={t('workspaceName')}
                  />
                  <input
                    value={workspace.defaultProjectPermission}
                    onChange={(event) =>
                      setWorkspace((prev) => ({ ...prev, defaultProjectPermission: event.target.value }))
                    }
                    className="rounded-lg border border-brand-200 bg-white px-3 py-2 dark:border-brand-900 dark:bg-slate-950"
                    placeholder={t('defaultProjectPermission')}
                  />
                  <input
                    value={workspace.invitation}
                    onChange={(event) =>
                      setWorkspace((prev) => ({ ...prev, invitation: event.target.value }))
                    }
                    className="rounded-lg border border-brand-200 bg-white px-3 py-2 dark:border-brand-900 dark:bg-slate-950"
                    placeholder={t('memberInvitationSettings')}
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setWorkspace((prev) => ({
                        ...prev,
                        ...sampleWorkspaceFields(),
                      }))
                    }
                    className="rounded-lg border border-dashed border-brand-300 px-3 py-1.5 text-xs font-medium text-brand-800 hover:bg-brand-50 dark:border-brand-700 dark:text-brand-200 dark:hover:bg-brand-950/50"
                  >
                    {t('fillSampleData')}
                  </button>
                  <button
                    type="button"
                    disabled={savingTab === 'workspace'}
                    onClick={() =>
                      runSave('workspace', () =>
                        apiPatchWorkspaceSettings({
                          workspace: {
                            name: workspace.name.trim(),
                            invitation: workspace.invitation.trim(),
                            defaultProjectPermission: workspace.defaultProjectPermission.trim(),
                          },
                        }),
                      )
                    }
                    className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                  >
                    {savingTab === 'workspace' ? '…' : t('saveChanges')}
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'integrations' && (
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-slate-900 dark:text-white">{t('settingsIntegrations')}</h3>
                <div className="grid gap-2 md:grid-cols-2">
                  {integrations.map((integration) => (
                    <div
                      key={integration.id}
                      className="rounded-xl border border-brand-200 p-3 text-xs dark:border-brand-900"
                    >
                      <p className="font-medium text-slate-700 dark:text-slate-200">{integration.name}</p>
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <span
                          className={`rounded-full px-2 py-0.5 ${
                            integration.connected
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                              : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                          }`}
                        >
                          {integration.connected ? t('connected') : t('disconnected')}
                        </span>
                        <button
                          type="button"
                          disabled={savingTab === `int-${integration.id}`}
                          onClick={() =>
                            runSave(`int-${integration.id}`, async () => {
                              const next = integrations.map((item) =>
                                item.id === integration.id
                                  ? { ...item, connected: !item.connected }
                                  : item,
                              );
                              setIntegrations(next);
                              await apiPatchWorkspaceSettings({
                                integrations: next.map((i) => ({
                                  id: i.id,
                                  name: i.name,
                                  connected: i.connected,
                                })),
                              });
                            })
                          }
                          className="shrink-0 rounded-md bg-brand-500 px-2.5 py-1 text-white disabled:opacity-60"
                        >
                          {integration.connected ? t('disconnect') : t('connect')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export default SettingsPage;
